import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useMatches } from 'react-router';
import { type RequestContext, type SerializeFrom } from '@cx-web/domain-types';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { getDefaultProduct } from '@cx-web/utils/getDefaultProduct';
import { captureException } from '@sentry/react-router';

import BettingInsights from '~/components/BettingInsights';
import SortingLayout from '~/components/BettingInsights/components/SortingLayout';
import { Error as ErrorComponent } from '~/components/error/Error.tsx';
import { Hero } from '~/components/hero/Hero.tsx';
import SearchWrapper from '~/components/SearchWrapper';
import { type MatchupCentreIndex } from '~/configuration/types';
import { createMatchupCentreConfig } from '~/pages/config';
import { matchupCentreIndex } from '~/search-domain/indexes';

import type { loader as rootLoader } from '../../../root-loader';

import { loader } from './loader';
import { meta } from './meta';

export default function MatchCentreScheduleRoute() {
  const matches = useMatches();
  const rootMatch = matches.find(match => /oddschecker-plus\/root$/.test(match.id));
  const rootLoaderData = rootMatch?.data as SerializeFrom<typeof rootLoader>;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const { geolocationInfo, siteConfig } = rootLoaderData;
  const {
    availableProducts,
    currentPath,
    monthlyProducts,
    repub,
    requestContext,
    resourcesUrl,
    subscriptionStatus,
    yearlyProducts,
  } = useLoaderData<typeof loader>();
  const config = createMatchupCentreConfig(requestContext as RequestContext);
  const userHasCancelledSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'CANCELLED',
  );

  const userHasActiveSubscription = requestContext.user.subscriptions.some(
    ({ status }: { status: string }) => status === 'ACTIVE' || status === 'TRIAL',
  );

  const defaultMonthlyProduct = getDefaultProduct(monthlyProducts);
  const defaultYearlyProduct = getDefaultProduct(yearlyProducts);

  const { t } = useTranslation();
  const { repubIs, repubPath } = getRepubMetaFromRepub(repub);

  return (
    <SortingLayout>
      <Hero
        currentPath={currentPath}
        hasActiveSubscription={userHasActiveSubscription}
        hasCancelledSubscription={userHasCancelledSubscription}
        monthlyProduct={defaultMonthlyProduct}
        repub={repub}
        repubPath={repubPath}
        shouldHideDetailsMobile={true}
        yearlyProduct={defaultYearlyProduct}
      >
        <div style={{ padding: '32px 0' }}>
          <h1>{t('CX_PLUS_MS_TITLE')}</h1>
          {repubIs.GZ && <p>{t('CX_PLUS_MS_HELPER_TEXT')}</p>}
        </div>
      </Hero>
      <ErrorBoundary
        fallbackRender={({ error }) => {
          captureException(error);
          return (
            <ErrorComponent
              indexName={matchupCentreIndex}
              message={(error as Error).message}
              title="Something went wrong"
            />
          );
        }}
      >
        <SearchWrapper<MatchupCentreIndex>
          config={config}
          indexName={matchupCentreIndex}
          repub={repub}
          state={geolocationInfo.subdivisionCode}
          userTZ={requestContext.user.TZ}
        >
          <BettingInsights<MatchupCentreIndex>
            availableProducts={availableProducts}
            bookmakers={siteConfig.bookmakers}
            config={config}
            currentPath={currentPath}
            indexName={matchupCentreIndex}
            redirectPath={currentPath ?? undefined}
            resourcesUrl={resourcesUrl}
            subscriptionStatus={subscriptionStatus}
            userHasCancelledSubscription={userHasCancelledSubscription}
            wallType={config.wallType}
          />
        </SearchWrapper>
      </ErrorBoundary>
    </SortingLayout>
  );
}

export { loader, meta };
